import React from 'react';
import { styled } from '@glitz/react';
import {
  H3,
  Paragraph,
  ResponsiveSize,
  ResponsiveLineHeight,
  TextLabel,
} from 'Shared/SharedComponents/atoms/Typography';
import Composition from 'Product/Models/Composition.type';
import CareInstructions from 'Product/Models/CareInstructions.type';
import { WashIcon } from 'Product/WashAndCare';
import { ButtonHeight, ButtonTheme, LinkButton } from 'Shared/TextButtons';
import { createGridClasses, createOffsetGridClassLarge, fillGridBins } from 'Shared/grid';
import * as style from 'Shared/Style';

type PropType = {
  compositionLabel: string;
  compositions: Composition[];
  careLabel: string;
  careInstructions: CareInstructions;
  isCompact: boolean;
};

const PartHeading = styled(TextLabel, {
  marginBottom: '16px',
});

const CompositionWrap = styled.div({
  ['@media ' + style.mediaMinLarge]: {
    borderTop: {
      color: style.colors.monochrome.black,
      style: 'solid',
      width: '2px',
    },
    paddingTop: '16px',
  },
})

const CompositionHeadingWrap = styled.div({
  marginBottom: '24px'
})

const CompositionItemWrap = styled.div({
  marginBottom: '24px'
})

const CareInstructionsWrap = styled.div({
  ['@media ' + style.mediaMinLarge]: {
    borderTop: {
      color: style.colors.monochrome.black,
      style: 'solid',
      width: '2px',
    },
    paddingTop: '16px',
  },
})

const CareInstructionsHeadingWrap = styled.div({
  marginBottom: '24px'
})

const CareInstructionsItemWrap = styled.div({
  marginBottom: '24px'
})

const CareInstructionWashItemWrap = styled.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '8px',
})

const CareInstructionWashItemText = styled(Paragraph, {
  whiteSpace: 'nowrap'
})

const CareInstructionsItem = styled(Paragraph, {
  marginBottom: '16px'
})

function MaterialsContent({ compositionLabel, compositions, careLabel, careInstructions, isCompact }: PropType) {
  const compositionBins = compositions.length > 2 ? 3 : 2;
  return (
    <div>
      {compositions && compositions.length > 0 && (
        <CompositionWrap className="grid subgrid-11">
          <CompositionHeadingWrap className={createGridClasses(12, 5)}>
            <H3 fontSize={ResponsiveSize.D32_M24}>{compositionLabel}</H3>
          </CompositionHeadingWrap>
          {compositions?.map((composition, index) => {
            return (
              <CompositionItemWrap
                key={index}
                className={fillGridBins(6, compositionBins, index, 6)}
              >
                <PartHeading>{composition.name}</PartHeading>
                {composition.materials?.map((material, index) => {
                  return (
                    <Paragraph
                      key={index}
                      fontSize={ResponsiveSize.D16_M16}
                      lineHeight={ResponsiveLineHeight.D130_M130}
                    >{`${material.percentage}% ${material.name}`}</Paragraph>
                  );
                })}
              </CompositionItemWrap>
            );
          })}
        </CompositionWrap>
      )}
      <CareInstructionsWrap className="grid subgrid-11">
        <CareInstructionsHeadingWrap className={createGridClasses(12, 5)}>
          <H3>{careLabel}</H3>
        </CareInstructionsHeadingWrap>
        {careInstructions.washItems && (
          <CareInstructionsItemWrap className={createGridClasses(12, 3, createOffsetGridClassLarge(6))}>
            <PartHeading>{careInstructions.labels.wash}</PartHeading>
            {careInstructions.washItems?.map((careInstruction, index) => {
              return (
                <CareInstructionWashItemWrap key={index}>
                  <WashIcon name={careInstruction.image} />
                  <CareInstructionWashItemText
                    fontSize={ResponsiveSize.D16_M16}
                    lineHeight={ResponsiveLineHeight.D130_M130}
                  >
                    {careInstruction.text}
                  </CareInstructionWashItemText>
                </CareInstructionWashItemWrap>
              );
            })}
          </CareInstructionsItemWrap>
        )}
        <CareInstructionsItemWrap className={createGridClasses(12, 3, createOffsetGridClassLarge(9))}>
          <PartHeading>{careInstructions.textHeading}</PartHeading>
          <CareInstructionsItem
            fontSize={ResponsiveSize.D16_M16}
            lineHeight={ResponsiveLineHeight.D130_M130}
          >
            {careInstructions.textIntro}
          </CareInstructionsItem>
          <LinkButton
            text={careInstructions.linkText}
            to={careInstructions.linkUrl}
            theme={ButtonTheme.WhiteToBlack}
            height={ButtonHeight.Big}
          />
        </CareInstructionsItemWrap>
      </CareInstructionsWrap>
    </div>
  );
}

export default MaterialsContent;
